// apps/web/src/features/crm/leads/activity/leadFeedEntries.tsx
//
// Pure mapping helpers (#1809) that turn raw `LeadActivityFeedItem`s into
// the shared `JournalTimeline` entry shape, plus the category + search
// projections the episode-feed filter runs client-side. Kept free of hooks
// so the mapping is unit-testable in isolation.
import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import {
  LeadActivityKind,
  type LeadActivityFeedItem,
  type LeadActivityReferences,
} from '@magimp/shared';
import type { JournalTimelineEntry } from '../../../../ui/index.js';
import { formatDateTime } from '../../../../i18n/index.js';

// WHY: the bundled i18next `TFunction` defaults to the `common` namespace, so a
// bare alias cannot type the `leads:`-prefixed keys this helper resolves. Bind
// the namespace tuple — mirrors `cargoColumns.tsx`'s `CargoT` pattern.
type LeadFeedT = TFunction<readonly ['leads', 'common']>;

/** Coarse category the type-filter buckets a feed row into. */
export type LeadFeedCategory = 'note' | 'call' | 'message' | 'file' | 'other';

export interface LeadFeedFallbacks {
  actorSystem: string;
  commentDeleted: string;
  fileDeleted: string;
  communicationDeleted: string;
  transcriptPlaceholder: string;
}

/** Map a feed kind onto the coarse category used by the type-filter. */
export function leadFeedCategory(kind: LeadActivityFeedItem['kind']): LeadFeedCategory {
  switch (kind) {
    case LeadActivityKind.LEAD_COMMENT_ADDED:
    case LeadActivityKind.LEAD_COMMENT_EDITED:
      return 'note';
    case LeadActivityKind.CALL:
      return 'call';
    case LeadActivityKind.MESSAGE:
      return 'message';
    case LeadActivityKind.LEAD_FILE_UPLOADED:
    case LeadActivityKind.LEAD_FILE_DELETED:
      return 'file';
    default:
      return 'other';
  }
}

/** Localized reference detail text for a feed row, or null when none applies. */
function describeReference(
  refs: LeadActivityReferences,
  kind: LeadActivityFeedItem['kind'],
  t: LeadFeedT,
  fallbacks: LeadFeedFallbacks,
): string | null {
  if (
    kind === LeadActivityKind.LEAD_COMMENT_ADDED ||
    kind === LeadActivityKind.LEAD_COMMENT_EDITED
  ) {
    if (refs.comment === undefined) return null;
    if (refs.comment === null) return fallbacks.commentDeleted;
    return refs.comment.bodyPreview;
  }
  if (kind === LeadActivityKind.LEAD_FILE_UPLOADED || kind === LeadActivityKind.LEAD_FILE_DELETED) {
    if (refs.file === undefined) return null;
    if (refs.file === null) return fallbacks.fileDeleted;
    return refs.file.fileName;
  }
  if (kind === LeadActivityKind.CALL || kind === LeadActivityKind.MESSAGE) {
    if (refs.communication === undefined) return null;
    if (refs.communication === null) return fallbacks.communicationDeleted;
    const c = refs.communication;
    const direction = t(`leads:activity.communications.direction.${c.direction}`);
    const channel = t(`leads:activity.communications.channel.${c.channel}`);
    return `${direction} · ${channel} — ${c.text}`;
  }
  return null;
}

/** Lowercased haystack (actor + kind label + reference text) for client-side search. */
export function leadFeedSearchText(
  item: LeadActivityFeedItem,
  t: LeadFeedT,
  fallbacks: LeadFeedFallbacks,
): string {
  const actor = item.actor?.name ?? fallbacks.actorSystem;
  const kindLabel = t(`leads:activity.kind.${item.kind}`);
  const ref = describeReference(item.references, item.kind, t, fallbacks) ?? '';
  const transcript =
    item.references.communication != null ? (item.references.communication.transcript ?? '') : '';
  return `${actor} ${kindLabel} ${ref} ${transcript}`.toLowerCase();
}

/** Transcript line for a CALL/MESSAGE row (real transcript or a placeholder). */
function communicationTranscript(
  refs: LeadActivityReferences,
  kind: LeadActivityFeedItem['kind'],
  fallbacks: LeadFeedFallbacks,
): string | null {
  if (kind !== LeadActivityKind.CALL && kind !== LeadActivityKind.MESSAGE) return null;
  if (refs.communication == null) return null;
  return refs.communication.transcript ?? fallbacks.transcriptPlaceholder;
}

/** Build the shared-timeline entries for a page of loaded feed items. */
export function buildLeadFeedEntries(
  items: readonly LeadActivityFeedItem[],
  t: LeadFeedT,
  fallbacks: LeadFeedFallbacks,
): JournalTimelineEntry[] {
  return items.map((item) => {
    const actor = item.actor?.name ?? fallbacks.actorSystem;
    const refDetail = describeReference(item.references, item.kind, t, fallbacks);
    const transcript = communicationTranscript(item.references, item.kind, fallbacks);
    const primary: ReactNode = (
      <>
        <strong>{actor}</strong> {t(`leads:activity.kind.${item.kind}`)}
      </>
    );
    const metadata: ReactNode = (
      <span
        style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        data-testid="lead-feed-entry-meta"
        data-kind={item.kind}
      >
        {refDetail != null && <span data-testid="lead-feed-entry-ref">{refDetail}</span>}
        {transcript != null && (
          <span style={{ color: 'var(--fg-2)' }} data-testid="lead-feed-entry-transcript">
            {transcript}
          </span>
        )}
        <time dateTime={item.occurredAt}>{formatDateTime(item.occurredAt)}</time>
      </span>
    );
    return { key: item.id, primary, metadata };
  });
}
